import {
  type IPayoutDetails,
  type IPayoutDetails2,
  type PayoutRefund,
  PayoutStatus,
  type PayoutTransaction,
} from "@gen/app-query";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import MuiLink from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import type { GridColDef } from "@mui/x-data-grid-pro";
import { Link } from "@tanstack/react-router";
import { type ReactNode, useMemo, useState } from "react";

import { PublicSvgIcon } from "~/components/public_svg_icon";
import { ReadableDataGrid } from "~/components/readable_data_grid";
import { useConfig } from "~/contexts/config";
import { Money } from "~/utilities/money";

import {
  formatPayoutDate,
  getPayoutRefundAmount,
  isAdminPayout,
} from "./payoutData";
import { downloadPayoutCsv } from "./payoutExport";

function CollapsibleSection({
  id,
  title,
  icon,
  count,
  countTestId,
  sx,
  children,
}: {
  id: string;
  title: string;
  icon: string;
  count: number;
  countTestId: string;
  sx?: object;
  children: ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <Stack id={id} spacing={2.5} sx={sx}>
      <Typography variant="h5SolidDivider" component="div" sx={{ p: 0 }}>
        <Box
          component="button"
          type="button"
          aria-expanded={isOpen}
          aria-controls={`${id}-content`}
          onClick={() => setIsOpen((open) => !open)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            width: "100%",
            p: 0,
            pb: 1.5,
            border: "none",
            background: "transparent",
            cursor: "pointer",
            textAlign: "left",
            // The heading typography lives on the Typography around this
            // button, and a button doesn't inherit font by default.
            font: "inherit",
            color: "text.primary",
          }}
          data-testid={`${id}-toggle-button`}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PublicSvgIcon name={icon} /> {title}
            <Typography
              variant="body1"
              component="span"
              sx={{ color: "text.secondary" }}
              data-testid={countTestId}
            >
              {count}
            </Typography>
          </Box>
          <PublicSvgIcon
            name="expand_less"
            inheritColor
            sx={{
              display: "flex",
              transform: isOpen ? "none" : "rotate(180deg)",
              transition: "transform 0.2s ease",
            }}
          />
        </Box>
      </Typography>

      <Collapse in={isOpen} id={`${id}-content`} unmountOnExit>
        {children}
      </Collapse>
    </Stack>
  );
}

export interface PayoutBreakdownProps {
  payoutDetails: IPayoutDetails | IPayoutDetails2;
}

export function PayoutBreakdown({ payoutDetails }: PayoutBreakdownProps) {
  const { localization } = useConfig();
  const theme = useTheme();

  const sectionSx = {
    mb: { xs: 3, lg: 5 },
    scrollMarginTop: theme.spacing(10),
  };

  const { payout, transactions, refunds } = payoutDetails;

  const isAdmin = isAdminPayout(payout);

  const getTransactionUrl = useMemo(
    () => (transactionID: number, clientId: string, v2ID?: string) => {
      if (!isAdmin) {
        return `/clients/${clientId}/f2f/transactions/${transactionID}`;
      }
      return v2ID
        ? `/admin/v2/transactions/${v2ID}`
        : `/admin/f2f/transactions/${transactionID}`;
    },
    [isAdmin],
  );

  const payoutAmount = isAdmin ? payout.payout_summary.amount : payout.amount;
  const isPayoutCompleted = payout.status === PayoutStatus.Completed;

  const transactionColumns = useMemo<GridColDef<PayoutTransaction>[]>(
    () => [
      {
        field: "funds_released",
        headerName: "Release Date",
        minWidth: 160,
        flex: 1,
        sortable: false,
        renderCell: (params) => (
          <Typography variant="body2">
            {formatPayoutDate(
              params.row.funds_released,
              localization.locale,
              localization.timeZone,
            )}
          </Typography>
        ),
      },
      {
        field: "id",
        headerName: "ID",
        minWidth: 140,
        flex: 1,
        sortable: false,
        renderCell: (params) => (
          <MuiLink
            component={Link}
            to={getTransactionUrl(
              params.row.id,
              params.row.client_id,
              params.row.v2_id,
            )}
            variant="body2"
            fontWeight="bold"
          >
            {params.row.v2_id ?? params.row.id}
          </MuiLink>
        ),
      },
      {
        field: "description",
        headerName: "Description",
        minWidth: 200,
        flex: 1,
        sortable: false,
        renderCell: (params) => (
          <Typography variant="body2">
            {params.row.description || "N/A"}
          </Typography>
        ),
      },
      {
        field: "client_name",
        headerName: "Client",
        minWidth: 200,
        flex: 1,
        sortable: false,
        align: "left",
        headerAlign: "left",
        renderCell: (params) => (
          <Typography variant="body2">
            {params.row.client_name || "N/A"}
          </Typography>
        ),
      },
      {
        field: "amount_released",
        headerName: "Released Amount",
        minWidth: 140,
        flex: 1,
        sortable: false,
        align: "right",
        headerAlign: "right",
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            {new Money(
              params.row.amount_released,
              params.row.currency,
            ).format()}
          </Typography>
        ),
      },
    ],
    [localization.locale, localization.timeZone, getTransactionUrl],
  );

  const refundColumns = useMemo<GridColDef<PayoutRefund>[]>(
    () => [
      {
        field: "refund_date",
        headerName: "Refund Date",
        minWidth: 160,
        flex: 1,
        sortable: false,
        renderCell: (params) => (
          <Typography variant="body2">
            {formatPayoutDate(
              params.row.refund.refunded,
              localization.locale,
              localization.timeZone,
            )}
          </Typography>
        ),
      },
      {
        field: "id",
        headerName: "ID",
        minWidth: 160,
        flex: 1,
        sortable: false,
        renderCell: (params) => (
          <Typography variant="body2">{params.row.refund.id}</Typography>
        ),
      },
      {
        field: "original_transaction",
        headerName: "Original Transaction",
        minWidth: 140,
        flex: 1,
        sortable: false,
        renderCell: (params) => (
          <MuiLink
            component={Link}
            to={getTransactionUrl(
              params.row.transaction_id,
              params.row.client_id,
              params.row.v2_id,
            )}
            variant="body2"
            fontWeight="bold"
          >
            {params.row.v2_id ?? params.row.transaction_id}
          </MuiLink>
        ),
      },
      {
        field: "client_name",
        headerName: "Client",
        minWidth: 200,
        flex: 1,
        sortable: false,
        align: "left",
        headerAlign: "left",
        renderCell: (params) => (
          <Typography variant="body2">
            {params.row.client_name || "N/A"}
          </Typography>
        ),
      },
      {
        field: "refunded_amount",
        headerName: "Refunded Amount",
        minWidth: 140,
        flex: 1,
        sortable: false,
        align: "right",
        headerAlign: "right",
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{ fontWeight: "bold", color: "error.main" }}
          >
            {new Money(
              getPayoutRefundAmount(params.row),
              params.row.currency,
            ).format()}
          </Typography>
        ),
      },
    ],
    [localization.locale, localization.timeZone, getTransactionUrl],
  );

  return (
    <>
      {isPayoutCompleted && (
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="text"
            onClick={() =>
              downloadPayoutCsv({
                payoutDetails,
                locale: localization.locale,
                timeZone: localization.timeZone,
              })
            }
            data-testid="payout-export-button"
            sx={{ px: 1, py: 0.75, borderRadius: 1 }}
            startIcon={
              <PublicSvgIcon
                name="ios_share"
                inheritColor
                sx={{ "& svg": { width: 12, height: 16 } }}
              />
            }
          >
            Export
          </Button>
        </Stack>
      )}
      <CollapsibleSection
        id="transactions"
        title="Transactions"
        icon="swap_horiz"
        count={transactions.length}
        countTestId="payout-transactions-count-text"
        sx={sectionSx}
      >
        <ReadableDataGrid<PayoutTransaction>
          dataTestId="payout-transactions-grid"
          storageKey="PayoutTransactions"
          columns={transactionColumns}
          rows={transactions}
          rowCount={transactions.length}
          showAllRows
          hideToolbar
          localeText={{
            noRowsLabel: "No Transactions",
          }}
        />
      </CollapsibleSection>
      {refunds.length > 0 && (
        <CollapsibleSection
          id="refunds"
          title="Refunds"
          icon="undo"
          count={refunds.length}
          countTestId="payout-refunds-count-text"
          sx={sectionSx}
        >
          <ReadableDataGrid<PayoutRefund>
            dataTestId="payout-refunds-grid"
            storageKey="PayoutRefunds"
            columns={refundColumns}
            rows={refunds}
            getRowId={(row) => row.refund.id}
            rowCount={refunds.length}
            showAllRows
            hideToolbar
            localeText={{
              noRowsLabel: "No Refunds",
            }}
          />
        </CollapsibleSection>
      )}
      <Typography
        variant="h5"
        component="div"
        sx={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PublicSvgIcon name="account_balance" /> Payout Total
        </Box>
        <Box
          component="span"
          sx={{
            fontWeight: "bold",
            pt: 1.5,
            borderTop: "2px solid",
            borderColor: "divider",
          }}
          data-testid="payout-total-text"
        >
          {new Money(payoutAmount, payout.currency).format()}
        </Box>
      </Typography>
    </>
  );
}
